import { z } from 'zod';

import { ApplicantDetails, AttachDocumentSchema, IStudyHistory } from '../applicant';
import { ApplicationRefereeRelation, ApplicationStatus, FeeStatus, FundingOption } from '../enums';
import { ApplicationTaskRoute, RouteStatus, SectionRoute, YourInformationTaskRoute } from '../route';

export const FundingPlanSchema = z.object({
  primaryFundingSource: z.nativeEnum(FundingOption),
  otherSourceOfFunding: z.string().nullish(),
  isSLCGrant: z.boolean().nullish(),
  isSLCLoan: z.boolean().nullish(),
  isPlanningToWork: z.boolean()
});

export function enhanceFundingPlanSchema(feeStatus: FeeStatus): z.ZodEffects<any> {
  let fundingSchema: z.ZodObject<any> = FundingPlanSchema;
  if (feeStatus === FeeStatus.Home) {
    fundingSchema = z.object({
      primaryFundingSource: z.nativeEnum(FundingOption),
      otherSourceOfFunding: z.string().nullish(),
      isSLCGrant: z.boolean(),
      isSLCLoan: z.boolean(),
      isPlanningToWork: z.boolean()
    });
  }
  return fundingSchema.superRefine((data, ctx) => {
    const { primaryFundingSource } = data;

    if (primaryFundingSource === FundingOption.Other) {
      if (!data.otherSourceOfFunding?.trim?.()?.length) {
        ctx.addIssue({
          code: 'custom',
          path: ['otherSourceOfFunding'],
          message: 'Description of other source of funding is required'
        });
      }
    }
    return true;
  });
}

export const PersonalStatementSchema = z.object({
  hasPersonalStatementDocument: z.boolean(),
  personalStatementDocument: AttachDocumentSchema.optional()
});

export const PersonalStatementSchemaWithDependencies = (
  fileName?: string
): z.ZodEffects<typeof PersonalStatementSchema> => {
  return PersonalStatementSchema.superRefine((data, ctx) => {
    if (data.hasPersonalStatementDocument && !fileName) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Personal statement file is required.`,
        path: ['fileUpload']
      });
    }
  });
};

export const RefereeSchema = z
  .object({
    name: z.string().min(1, "Name can't be empty"),
    email: z.string().email(),
    phone: z
      .string()
      .includes('-')
      .min(2)
      .refine(
        value => {
          const [code, phone] = value.split('-');
          return code && phone && code.length && phone.length && /^\d+$/.test(code) && /^\d+$/.test(phone);
        },
        { message: 'Invalid phone number.' }
      ),
    relationship: z.nativeEnum(ApplicationRefereeRelation, { message: 'Please provide the value' }),
    otherRelationshipDetails: z.string().optional(),
    addressLine1: z.string().min(1, "Address can't be empty"),
    addressLine2: z.string().optional(),
    addressLine3: z.string().optional(),
    city: z.string().min(1, "City can't be empty"),
    state: z.string().optional(),
    postcode: z.string().min(4, 'Postcode must be at least 4 characters'),
    country: z.string().min(1, "Country can't be empty"),
    organisation: z.string().min(1, "Organisation can't be empty"),
    occupation: z.string().min(1, "Occupation can't be empty"),
    yearsKnown: z.number()
  })
  .superRefine((data, ctx) => {
    if (data.relationship === ApplicationRefereeRelation.Other && !data.otherRelationshipDetails) {
      ctx.addIssue({
        code: 'custom',
        path: ['otherRelationshipDetails'],
        message: 'Other Relationship details are required'
      });
    }
  });

export const ReferencesSchema = z.object({
  hasReferee: z.boolean(),
  references: z.array(RefereeSchema).nullish(),
  referenceLetterDocument: AttachDocumentSchema.optional()
});

export const AgentSchema = z.object({
  name: z.string().min(1, "Name can't be empty"),
  agency: z.string().min(1, "Agency can't be empty"),
  email: z.string().min(1, "Email can't be empty").email(),
  consent: z.boolean()
});

export const OtherPresentative = z.object({
  relation: z.string().min(1, "Relation can't be empty"),
  name: z.string().min(1, "Name can't be empty")
});

export const RepresentationSchema = z
  .object({
    hasRepresentative: z.boolean({
      required_error: 'Please select whether anyone is helping you submit your application'
    }),
    isHelpedByAgent: z.boolean().optional(),
    isHelpedByFamilyMember: z.boolean().optional(),
    isHelpedByFriend: z.boolean().optional(),
    isHelpedBySchoolCounsellor: z.boolean().optional(),
    isHelpedByTeacher: z.boolean().optional(),
    isHelpedByStudent: z.boolean().optional(),
    isHelpedByOther: z.boolean().optional(),
    studentName: z.string().min(1, "Student name can't be empty").nullish(),
    teacherName: z.string().min(1, "Teacher name can't be empty").nullish(),
    schoolCounsellorName: z.string().min(1, "School counsellor name can't be empty").nullish(),
    friendName: z.string().min(1, "Friend name can't be empty").nullish(),
    familyMemberName: z.string().min(1, "Family member can't be empty").nullish(),
    agent: AgentSchema.nullish(),
    otherPresentative: OtherPresentative.nullish()
  })
  .superRefine((data, ctx) => {
    if (data.hasRepresentative === true) {
      const helpOptions = [
        data.isHelpedByAgent,
        data.isHelpedByFamilyMember,
        data.isHelpedByFriend,
        data.isHelpedBySchoolCounsellor,
        data.isHelpedByTeacher,
        data.isHelpedByStudent,
        data.isHelpedByOther
      ];
      if (!helpOptions.some(option => option === true)) {
        ctx.addIssue({
          code: 'custom',
          path: ['representationType'],
          message: 'At least one representation type must be selected'
        });
      }
    }

    if (data.isHelpedByAgent === true && !data.agent) {
      ctx.addIssue({
        code: 'custom',
        path: ['agencyName'],
        message: 'Agent detail are required'
      });
    }
    if (data.isHelpedByFamilyMember === true && !data.familyMemberName) {
      ctx.addIssue({
        code: 'custom',
        path: ['familyMemberName'],
        message: 'Family member name is required'
      });
    }
    if (data.isHelpedByFriend === true && !data.friendName) {
      ctx.addIssue({
        code: 'custom',
        path: ['friendName'],
        message: 'Friend name is required'
      });
    }
    if (data.isHelpedBySchoolCounsellor === true && !data.schoolCounsellorName) {
      ctx.addIssue({
        code: 'custom',
        path: ['schoolCounsellorName'],
        message: 'School counsellor name is required'
      });
    }
    if (data.isHelpedByTeacher === true && !data.teacherName) {
      ctx.addIssue({
        code: 'custom',
        path: ['teacherName'],
        message: 'Teacher name is required'
      });
    }
    if (data.isHelpedByStudent === true && !data.studentName) {
      ctx.addIssue({
        code: 'custom',
        path: ['studentName'],
        message: 'Student name is required'
      });
    }

    if (data.isHelpedByOther === true && !data.otherPresentative) {
      ctx.addIssue({
        code: 'custom',
        path: ['otherPresentative'],
        message: 'Other person details are required'
      });
    }
  });

export const RepresentationFormSchema = z.object({
  representative: RepresentationSchema
});

export const DeclarationsSchema = z
  .object({
    interruptedStudy: z.boolean(),
    criminalConvictions: z.boolean().nullish(),
    detailedInterruptionReason: z.string().nullish(),
    offenceDetails: z.string().nullish()
  })
  .superRefine((data, ctx) => {
    // Validate that detailedInterruptionReason is provided when interruptedStudy is true
    if (data.interruptedStudy === true) {
      if (!data.detailedInterruptionReason?.trim?.()?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Detailed interruption reason is required when study was interrupted',
          path: ['detailedInterruptionReason']
        });
      }
    }

    // Validate that offenceDetails is provided when criminalConvictions is true
    if (data?.criminalConvictions === true) {
      if (!data.offenceDetails?.trim?.()?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Offence details are required when criminal convictions exist',
          path: ['offenceDetails']
        });
      }
    }
    return true;
  });

export const InformationProcessingConsentSchema = z.object({
  informationProcessingConsent: z.boolean().refine(value => value === true, {
    message: 'You must agree to the information processing consent.'
  })
});

export const PrivacyPolicyConsentSchema = z.object({
  privacyPolicyConsent: z.boolean().refine(value => value === true, {
    message: 'You must agree to the privacy policy.'
  })
});

export const TermsAndConditionsConsentSchema = z.object({
  termsAndConditionsConsent: z.boolean().refine(value => value === true, {
    message: 'You must agree to the terms and conditions.'
  })
});

export const AccuracyOfDataConsentSchema = z.object({
  accuracyOfDataConsent: z.boolean().refine(value => value === true, {
    message: 'You must confirm the accuracy of the provided data.'
  })
});

export const ConsentSchema = z.object({
  ...InformationProcessingConsentSchema.shape,
  ...PrivacyPolicyConsentSchema.shape,
  ...TermsAndConditionsConsentSchema.shape,
  ...AccuracyOfDataConsentSchema.shape
});

export type ApplicationContextType = {
  intakeId: string;
  intendedLevelOfStudy: string;
  isApplicationLocked: boolean;
  universityName: string;
};

export interface Application
  extends IFundingPlan,
    IPersonalStatement,
    IReferences,
    IRepresentationForm,
    IConsent,
    IDeclarations,
    Omit<ApplicantDetails, 'routeStatus'> {
  userId: string;
  applicationId: string;
  applicationStatus: ApplicationStatus;
  universityName: string;
  courseName: string;
  routeStatus: Partial<Record<ApplicationTaskRoute, RouteStatus>> &
    Partial<Record<YourInformationTaskRoute.StudyPlans, RouteStatus>> &
    Partial<Record<SectionRoute, RouteStatus>>;
  studyHistory: IStudyHistory;
  hasAgent: string; // probably should be boolean
  agentId?: string;
  agentName?: string;
}

export type NotSubmittableApplication = Omit<Application, keyof ApplicantDetails>;

export const StaffAssignAgentSchema = z.object({
  agentId: z.string()
});

export interface IStaffAssignAgentSchema extends z.infer<typeof StaffAssignAgentSchema> {}

/* eslint-disable @typescript-eslint/no-empty-object-type */

export interface IFundingPlan extends z.infer<typeof FundingPlanSchema> {}
export interface IPersonalStatement extends z.infer<typeof PersonalStatementSchema> {}
export interface IReferences extends z.infer<typeof ReferencesSchema> {}
export interface IReferee extends z.infer<typeof RefereeSchema> {}

export interface IDeclarations extends z.infer<typeof DeclarationsSchema> {}
export interface IConsent extends z.infer<typeof ConsentSchema> {}
export interface IRepresentation extends z.infer<typeof RepresentationSchema> {}
export interface IRepresentationForm extends z.infer<typeof RepresentationFormSchema> {}
